import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button } from '../common';
import { useNotificationService } from '../Notification';
import { ConfirmDialog } from '../Notification/ConfirmDialog';
import { useSettingsTranslation } from '../../modules/i18n/hooks/useTranslation';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const { showSuccess, showError } = useNotificationService();
  const { t } = useSettingsTranslation();

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required(t('password.validation.currentRequired')),
    newPassword: Yup.string()
      .min(8, t('password.validation.minLength'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t('password.validation.pattern'))
      .required(t('password.validation.newRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('password.validation.mismatch'))
      .required(t('password.validation.confirmRequired'))
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [resetFormCallback, setResetFormCallback] = useState<(() => void) | null>(null);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const initialValues: FormValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (values: FormValues, { setSubmitting, resetForm }: any) => {
    try {
      console.log('Changing password:', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      // Password change is handled by Keycloak directly
      // Redirect to Keycloak's account management page
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || '/keycloak';
      const keycloakRealm = process.env.REACT_APP_KEYCLOAK_REALM ?? 'promete';
      const keycloakAccountUrl = `${keycloakUrl}/realms/${keycloakRealm}/account/#/security/password`;

      showSuccess(t('password.messages.changeSuccess'), t('password.messages.redirectingToKeycloak'));

      // Reset form
      resetForm();

      // Redirect to Keycloak account management page after a short delay
      setTimeout(() => {
        window.location.href = keycloakAccountUrl;
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      showError(t('password.messages.changeError'), t('password.messages.changeErrorDesc'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm: () => void) => {
    setResetFormCallback(() => resetForm);
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    if (resetFormCallback) {
      resetFormCallback();
    }
    setShowCancelDialog(false);
    setResetFormCallback(null);
  };

  const handleCancelDialog = () => {
    setShowCancelDialog(false);
    setResetFormCallback(null);
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const strengthMap = {
      0: { label: '', color: '' },
      1: { label: t('password.strength.veryWeak'), color: 'bg-red-500' },
      2: { label: t('password.strength.weak'), color: 'bg-orange-500' },
      3: { label: t('password.strength.medium'), color: 'bg-yellow-500' },
      4: { label: t('password.strength.strong'), color: 'bg-blue-500' },
      5: { label: t('password.strength.veryStrong'), color: 'bg-green-500' }
    };

    return { strength, ...strengthMap[strength as keyof typeof strengthMap] };
  };

  return (
    <main className='flex h-full flex-col border border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Header - Fixed height - Hidden on mobile */}
      {!isMobile && (
        <div className='flex-shrink-0 border-b border-gray-200 p-4 dark:border-border-dark'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='flex-shrink-0 text-gray-500 dark:text-dark-text-secondary'>
                <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                  />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </div>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-dark-text'>{t('title')}</h2>
            </div>
          </div>
        </div>
      )}

      {/* Content - Scrollable area */}
      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin'>
        <div className={isMobile ? 'flex-1 p-0' : 'flex-1 p-4 sm:p-6 lg:p-8'}>
          <div className={isMobile ? 'w-full' : 'mx-auto max-w-2xl'}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ isSubmitting, resetForm, values, dirty }) => {
                const passwordStrength = getPasswordStrength(values.newPassword);

                return (
                  <Form className='flex h-full flex-col' id='change-password-form'>
                    <div className='flex-1'>
                      <div className='overflow-hidden'>
                        <div className={isMobile ? 'px-4 py-6' : 'px-6 py-6'}>
                          <div className='space-y-4 sm:space-y-6'>
                            <div>
                              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>{t('password.fields.current')} *</label>
                              <div className='relative'>
                                <Field
                                  name='currentPassword'
                                  type={showPasswords.current ? 'text' : 'password'}
                                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text'
                                  placeholder={t('password.placeholders.current')}
                                />
                                <button
                                  type='button'
                                  onClick={() => togglePasswordVisibility('current')}
                                  className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary'
                                  title={showPasswords.current ? t('password.actions.hidePassword') : t('password.actions.showPassword')}
                                >
                                  {showPasswords.current ? (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                                      />
                                    </svg>
                                  ) : (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              <ErrorMessage name='currentPassword' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                            </div>

                            <div>
                              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>{t('password.fields.new')} *</label>
                              <div className='relative'>
                                <Field
                                  name='newPassword'
                                  type={showPasswords.new ? 'text' : 'password'}
                                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text'
                                  placeholder={t('password.placeholders.new')}
                                />
                                <button
                                  type='button'
                                  onClick={() => togglePasswordVisibility('new')}
                                  className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary'
                                  title={showPasswords.new ? t('password.actions.hidePassword') : t('password.actions.showPassword')}
                                >
                                  {showPasswords.new ? (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                                      />
                                    </svg>
                                  ) : (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>

                              {values.newPassword && (
                                <div className='mt-2'>
                                  <div className='flex items-center space-x-2'>
                                    <div className='h-2 flex-1 rounded-full bg-gray-200 dark:bg-dark-background-tertiary'>
                                      <div
                                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                        style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                                      />
                                    </div>
                                    <span className='min-w-0 text-xs text-gray-600 dark:text-dark-text-secondary'>{passwordStrength.label}</span>
                                  </div>
                                </div>
                              )}

                              <ErrorMessage name='newPassword' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                            </div>

                            <div>
                              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>{t('password.fields.confirm')} *</label>
                              <div className='relative'>
                                <Field
                                  name='confirmPassword'
                                  type={showPasswords.confirm ? 'text' : 'password'}
                                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text'
                                  placeholder={t('password.placeholders.confirm')}
                                />
                                <button
                                  type='button'
                                  onClick={() => togglePasswordVisibility('confirm')}
                                  className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary'
                                  title={showPasswords.confirm ? t('password.actions.hidePassword') : t('password.actions.showPassword')}
                                >
                                  {showPasswords.confirm ? (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                                      />
                                    </svg>
                                  ) : (
                                    <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              <ErrorMessage name='confirmPassword' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons - Desktop only */}
                    {!isMobile && (
                      <div className='mt-6 px-6'>
                        <div className='mx-auto max-w-2xl'>
                          <div className='flex justify-end space-x-3'>
                            <Button type='button' variant='outline' onClick={() => handleCancel(resetForm)} disabled={isSubmitting} className='w-40'>
                              {t('password.actions.cancel')}
                            </Button>
                            <Button type='submit' disabled={isSubmitting || !dirty} className='w-40'>
                              {isSubmitting ? (
                                <div className='flex items-center'>
                                  <svg className='-ml-1 mr-2 h-4 w-4 animate-spin text-white' fill='none' viewBox='0 0 24 24'>
                                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                                    <path
                                      className='opacity-75'
                                      fill='currentColor'
                                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    ></path>
                                  </svg>
                                  {t('password.actions.processing')}
                                </div>
                              ) : (
                                t('password.actions.change')
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>

        {/* Security Tips */}
        <div className={isMobile ? 'mb-20 mt-6 px-4' : 'my-6 px-6'}>
          <div className={isMobile ? 'w-full' : 'mx-auto max-w-2xl'}>
            <div className='rounded-lg border border-gray-200 bg-gray-50 dark:border-border-dark dark:bg-dark-background-tertiary/50'>
              <div className='px-4 py-6 sm:px-6'>
                <h3 className='mb-4 text-lg font-medium text-gray-900 dark:text-dark-text'>{t('password.securityTips.title')}</h3>
                <ul className='space-y-2 text-sm text-gray-600 dark:text-dark-text-secondary'>
                  <li className='flex items-start'>
                    <svg className='mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                      />
                    </svg>
                    {t('password.securityTips.tip1')}
                  </li>
                  <li className='flex items-start'>
                    <svg className='mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                      />
                    </svg>
                    {t('password.securityTips.tip2')}
                  </li>
                  <li className='flex items-start'>
                    <svg className='mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                      />
                    </svg>
                    {t('password.securityTips.tip3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom buttons for mobile */}
        {isMobile && (
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ resetForm }) => (
              <div className='fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4 dark:border-border-dark dark:bg-dark-background-secondary'>
                <div className='flex justify-between space-x-3'>
                  <Button type='button' variant='outline' onClick={() => handleCancel(resetForm)} className='flex-1'>
                    {t('password.actions.cancel')}
                  </Button>
                  <Button type='submit' form='change-password-form' className='flex-1'>
                    {t('password.actions.change')}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title={t('profile.dialog.confirmCancel')}
        message={t('profile.dialog.confirmCancelMessage')}
        confirmText={t('profile.dialog.confirmCancelButton')}
        cancelText={t('profile.dialog.continueEditing')}
        confirmVariant='danger'
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelDialog}
      />
    </main>
  );
};

export { ChangePassword };
export default ChangePassword;
